import _ from 'lodash';
import { getDefaultMaxCapsuleDuration } from '@/services/systemConfiguration.utilities';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { LOGIC, LOGIC_KEY, NO_MATCH_PROPERTY } from '@/tools/compositeSearch/compositeSearch.constants';
import { getToolConfigParams } from '@/tools/tool.utilities';
import { splitDuration } from '@seeqdev/utils';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type { CompositeLogic } from '@/tools/compositeSearch/compositeSearch.constants';
import type { CompositeSearchState } from '@/tools/compositeSearch/compositeSearch.store';
import type {
  ToolConditionDuration,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
  ToolParameter,
} from '@/tools/tool.types';

const PARAMETER_DEFINITION_KEYS = ['conditionA', 'conditionB'];

interface MaxDurations {
  output: FrontendDuration | undefined;
  conditionA: FrontendDuration | undefined;
  conditionB: FrontendDuration | undefined;
  overrideA: FrontendDuration | undefined;
  overrideB: FrontendDuration | undefined;
}

/** Formats a duration as a formula fragment, e.g. `5min`. */
export function formatDurationFragment(duration: { value: string | number; units: string }): string {
  return `${duration.value}${duration.units}`;
}

/** Reads the maximum duration from a condition item's metadata, normalizing string/object backend shapes. */
export function getConditionMaximumDuration(item: ToolItem | undefined): FrontendDuration | undefined {
  const maximumDuration =
    item?.conditionMetadata?.maximumDuration ??
    (item as (ToolItem & Record<string, unknown>) | undefined)?.maximumDuration;

  if (_.isNil(maximumDuration)) {
    return undefined;
  }

  if (_.isString(maximumDuration)) {
    return splitDuration(maximumDuration);
  }

  const durationObj = maximumDuration as Record<string, unknown>;
  const value = durationObj?.value as number | undefined;
  const units = (durationObj?.uom as string | undefined) ?? (durationObj?.units as string | undefined);

  if (value == null || units == null) {
    return undefined;
  }

  return { value, units };
}

/** Converts a backend-shaped `{ value, uom }` duration (e.g. from `conditionMetadata`) to the frontend shape. */
export function toFrontendMaximumDuration(
  backendDuration: ToolConditionDuration | null | undefined,
): FrontendDuration | undefined {
  return backendDuration?.value != null && backendDuration?.uom != null
    ? { value: backendDuration.value, units: backendDuration.uom }
    : undefined;
}

/** Falls back to the system-configured default maximum capsule duration when no override is set. */
export function getMaximumDurationOverrideOrDefault(
  override: FrontendDuration | undefined,
): FrontendDuration | undefined {
  return override || getDefaultMaxCapsuleDuration();
}

/**
 * Determines whether an input maximum duration override is required for the given condition slot.
 */
export function isMaximumDurationOverrideRequired(
  slot: 'A' | 'B',
  logicType: string | undefined,
  conditionPresent: boolean,
  originalMaximumDuration: FrontendDuration | { value: null } = { value: null },
  keepProperties = false,
  matchProperty = NO_MATCH_PROPERTY,
): boolean {
  const { value } = originalMaximumDuration;
  if (!conditionPresent) {
    return false;
  }

  switch (logicType) {
    case LOGIC_KEY.UNION:
      return false;
    case LOGIC_KEY.INTERSECTION:
      return _.isNil(value) && (keepProperties || matchProperty !== NO_MATCH_PROPERTY);
    case LOGIC_KEY.JOIN:
      return false;
    case LOGIC_KEY.OVERLAPPED_BY: // Touches
      return slot === 'A' && _.isNil(value);
    case LOGIC_KEY.NOT_OVERLAPPED_BY: // Outside
      return _.isNil(value);
    case LOGIC_KEY.ENCLOSES: // Inside
      return slot === 'B' && _.isNil(value);
    case LOGIC_KEY.SUBTRACT: // Minus
      return false;
    default:
      return false;
  }
}

/** Whether logic's operator text appears in the formula, excluding formulas matching another logic's operator. */
function matchesOperator(formula: string, logic: CompositeLogic): boolean {
  const includesOperator = formula.indexOf(logic.operator) > -1;
  const notExcluded = !logic.exclude || formula.indexOf(logic.exclude) === -1;

  return includesOperator && notExcluded;
}

/**
 * We correct all the legacy selectedOperators that were the inverse of another. By convention, those ended in
 * _INVERSE, and were identical to the primary operator except for the placement of $a and $b.
 */
export function correctLegacySelectOperators(
  selectedOperator: string | undefined,
  parameters: ToolParameter[],
): { selectedOperator: string | undefined; parameters: ToolParameter[] } {
  if (selectedOperator && _.endsWith(selectedOperator, '_INVERSE') && parameters.length >= 2) {
    const swapped = parameters.map((parameter) => ({ ...parameter }));
    [swapped[0].item, swapped[1].item] = [swapped[1].item, swapped[0].item];

    return { selectedOperator: _.replace(selectedOperator, '_INVERSE', ''), parameters: swapped };
  }

  return { selectedOperator, parameters };
}

export interface CompositeSearchFormulaDerivedState {
  selectedOperator: string | undefined;
  keepProperties: boolean;
  matchProperty: string;
  inclusiveA?: boolean;
  inclusiveB?: boolean;
  useEarliestStart?: boolean;
  isUpgradedUnionCondition?: boolean;
  maximumDurationOverrideA?: FrontendDuration;
  maximumDurationOverrideB?: FrontendDuration;
}

/**
 * Derives store state from a saved formula: the logic operator, Join inclusivity/earliest-start, the
 * pre-Conditions-2.0 Union upgrade flag, advanced-parameter formula fragments, and per-condition maximum
 * duration overrides.
 */
export function deriveStateFromFormula(formula: string): CompositeSearchFormulaDerivedState {
  const selectedOperator = _.chain(LOGIC)
    .filter((logic) => matchesOperator(formula, logic))
    .map('key')
    .first()
    .value() as string | undefined;

  const result: CompositeSearchFormulaDerivedState = {
    selectedOperator,
    keepProperties: formula.indexOf('keepProperties()') !== -1,
    matchProperty: /.*'(.*)'.*/.exec(formula)?.[1] ?? NO_MATCH_PROPERTY,
  };

  switch (selectedOperator) {
    // Restore inclusivity of Join conditions
    case LOGIC_KEY.JOIN:
      result.inclusiveA = !formula.match(/\$a.afterEnd\(0ns\)/);
      result.inclusiveB = !formula.match(/\$b.beforeStart\(0ns\)/);
      result.useEarliestStart = !!formula.match(/.*\.join\(.*, .*, true.*/);
      break;

    // Determine if this is an upgraded pre-Conditions 2.0 Union
    case LOGIC_KEY.UNION:
      result.isUpgradedUnionCondition = !!formula.match(/\.removeLongerThan\(.*\)/);
      break;
  }

  const maxDurA = splitDuration(_.get(/\$a\.setMaximumDuration\((\w.*?)\)/.exec(formula), '1'));
  if (maxDurA) {
    result.maximumDurationOverrideA = maxDurA;
  }
  const maxDurB = splitDuration(_.get(/\$b\.setMaximumDuration\((\w.*?)\)/.exec(formula), '1'));
  if (maxDurB) {
    result.maximumDurationOverrideB = maxDurB;
  }

  return result;
}

/** Indicates whether the store has all the required parameters to generate a formula. */
function canGenerateFormula(
  inputConditions: { a: ToolItem | undefined; b: ToolItem | undefined },
  maximumDurations: MaxDurations,
  selectedOperator: string | undefined,
): boolean {
  const { value: conditionAMaximumDurationValue = null } = maximumDurations.conditionA || {};
  const { value: conditionBMaximumDurationValue = null } = maximumDurations.conditionB || {};
  const { value: conditionAMaximumDurationOverrideValue = null } = maximumDurations.overrideA || {};
  const { value: conditionBMaximumDurationOverrideValue = null } = maximumDurations.overrideB || {};
  const { value: outputMaximumDurationValue = null } = maximumDurations.output || {};

  const isConditionABounded = !_.isNil(conditionAMaximumDurationValue);
  const isConditionBBounded = !_.isNil(conditionBMaximumDurationValue);
  const conditionAMaximumDurationHasOverride = !_.isNil(conditionAMaximumDurationOverrideValue);
  const conditionBMaximumDurationHasOverride = !_.isNil(conditionBMaximumDurationOverrideValue);
  const haveMaximumDuration = !_.isNil(outputMaximumDurationValue);

  const haveMaximumDurationForA = isConditionABounded || conditionAMaximumDurationHasOverride;
  const haveMaximumDurationForB = isConditionBBounded || conditionBMaximumDurationHasOverride;

  let operatorRequirementsSatisfied = false;
  switch (selectedOperator) {
    case LOGIC_KEY.JOIN:
      operatorRequirementsSatisfied = haveMaximumDuration;
      break;
    case LOGIC_KEY.OVERLAPPED_BY: // Touches
      operatorRequirementsSatisfied = haveMaximumDurationForA;
      break;
    case LOGIC_KEY.NOT_OVERLAPPED_BY: // Outside
      operatorRequirementsSatisfied = haveMaximumDurationForA && haveMaximumDurationForB;
      break;
    case LOGIC_KEY.ENCLOSES: // Inside
      operatorRequirementsSatisfied = haveMaximumDurationForB;
      break;
    case LOGIC_KEY.UNION:
    case LOGIC_KEY.INTERSECTION:
    case LOGIC_KEY.SUBTRACT: // Minus
      operatorRequirementsSatisfied = true;
      break;
  }

  return Boolean(inputConditions.a && inputConditions.b && selectedOperator && operatorRequirementsSatisfied);
}

function formulaArgument(
  arg: string,
  maximumDuration: FrontendDuration | undefined,
  exclusiveFragment: string,
): string {
  const maximumDurationFragment = !_.isNil(maximumDuration?.value)
    ? `.setMaximumDuration(${formatDurationFragment(maximumDuration!)})`
    : '';

  return `${arg}${exclusiveFragment}${maximumDurationFragment}`;
}

interface MakeFormulaParams {
  maximumDurations: MaxDurations;
  selectedOperator: string;
  isUpgradedUnionCondition: boolean;
  inclusive: { a: boolean; b: boolean };
  overridesRequired: { a: boolean; b: boolean };
  matchProperty: string | undefined;
  keepProperties: boolean;
  useEarliestStart: boolean;
}

function makeFormula({
  maximumDurations,
  selectedOperator,
  isUpgradedUnionCondition,
  inclusive,
  overridesRequired,
  matchProperty,
  keepProperties,
  useEarliestStart,
}: MakeFormulaParams): string {
  const stringOutputMaximumDuration = !_.isNil(maximumDurations.output?.value)
    ? formatDurationFragment(maximumDurations.output!)
    : '';

  const overrideA = overridesRequired.a ? maximumDurations.overrideA : undefined;
  const overrideB = overridesRequired.b ? maximumDurations.overrideB : undefined;
  const exclusiveFragmentA = !inclusive.a && selectedOperator === LOGIC_KEY.JOIN ? '.afterEnd(0ns)' : '';
  const exclusiveFragmentB = !inclusive.b && selectedOperator === LOGIC_KEY.JOIN ? '.beforeStart(0ns)' : '';

  const argumentA = formulaArgument('$a', overrideA, exclusiveFragmentA);
  const argumentB = formulaArgument('$b', overrideB, exclusiveFragmentB);

  const { formula, withMaximumDuration } = _.find(LOGIC, ({ key }) => key === selectedOperator)!;
  const formulaArguments: [string, string, string?, string?, boolean?, string?] = [
    argumentA,
    argumentB,
    matchProperty,
    keepProperties ? 'keepProperties()' : undefined,
    useEarliestStart,
    withMaximumDuration ? stringOutputMaximumDuration : undefined,
  ];
  const operatorFragment = formula(...formulaArguments);

  // If this is a pre-Conditions 2.0 Union composite condition (and thus still has a maximum duration) we need
  // to preserve that maximum duration in the generated formula.
  const upgradedUnionMaxDurationFragment = isUpgradedUnionCondition
    ? `.removeLongerThan(${stringOutputMaximumDuration})`
    : '';

  return `${operatorFragment}${upgradedUnionMaxDurationFragment}`;
}

/**
 * Computes the formula this tool's current state represents, along with the parameters (condition A/B ids) the
 * formula references. Returns `undefined` when the current state doesn't have enough information yet (e.g. a
 * condition or the logic operator hasn't been selected, or a required maximum duration is missing).
 */
export function buildCompositeSearchFormula(
  state: CompositeSearchState,
): { formula: string; parameters: { a: string; b: string } } | undefined {
  const { selectedOperator } = state;
  const inputConditions = { a: state.conditionA, b: state.conditionB };
  const maximumDurations: MaxDurations = {
    output: state.maximumDuration ?? undefined,
    conditionA: getConditionMaximumDuration(inputConditions.a),
    conditionB: getConditionMaximumDuration(inputConditions.b),
    overrideA: getMaximumDurationOverrideOrDefault(state.maximumDurationOverrideA),
    overrideB: getMaximumDurationOverrideOrDefault(state.maximumDurationOverrideB),
  };

  if (!canGenerateFormula(inputConditions, maximumDurations, selectedOperator)) {
    return undefined;
  }

  const inclusive = { a: state.inclusiveA, b: state.inclusiveB };
  const overridesRequired = {
    a: isMaximumDurationOverrideRequired(
      'A',
      selectedOperator,
      !!inputConditions.a,
      maximumDurations.conditionA,
      state.keepProperties,
      state.matchProperty,
    ),
    b: isMaximumDurationOverrideRequired('B', selectedOperator, !!inputConditions.b, maximumDurations.conditionB),
  };
  const isUpgradedUnionCondition = state.isUpgradedUnionCondition && selectedOperator === LOGIC_KEY.UNION;

  return {
    formula: makeFormula({
      maximumDurations,
      selectedOperator: selectedOperator!,
      isUpgradedUnionCondition,
      inclusive,
      overridesRequired,
      matchProperty: state.matchProperty === NO_MATCH_PROPERTY ? undefined : state.matchProperty,
      keepProperties: state.keepProperties,
      useEarliestStart: state.useEarliestStart,
    }),
    parameters: {
      a: inputConditions.a!.id!,
      b: inputConditions.b!.id!,
    },
  };
}

/**
 * Builds the UI config object to persist for a composite search calculated item. Composite search gets everything
 * it needs from the formula/parameters, so only `type` and `advancedParametersCollapsed` are kept.
 */
export function buildCompositeSearchConfigParams(state: CompositeSearchState): ToolConfigParams {
  const config = getToolConfigParams({
    serializedState: state as unknown as Record<string, ToolConfigValue>,
    toolType: TREND_TOOLS.COMPOSITE_SEARCH,
    parameterDefinitionKeys: PARAMETER_DEFINITION_KEYS,
    advancedParametersCollapsed: state.advancedParametersCollapsed,
  });
  return { type: config.type, advancedParametersCollapsed: config.advancedParametersCollapsed };
}
